import { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  connectGoogleCalendar,
  syncGoogleCalendarBusy,
  pushGoogleCalendarBusy
} from '../../../services/calendarSyncClient';

const parseCalendarActionOptions = (overrideUserIdOrOptions) => {
  let overrideUserId = null;
  let spaceId = null;
  let pullGoogle = true;
  let refreshBlocks = true;

  if (typeof overrideUserIdOrOptions === 'string') {
    overrideUserId = overrideUserIdOrOptions.trim() || null;
  } else if (overrideUserIdOrOptions && typeof overrideUserIdOrOptions === 'object') {
    overrideUserId =
      overrideUserIdOrOptions.userId ||
      overrideUserIdOrOptions.email ||
      null;
    spaceId = overrideUserIdOrOptions.spaceId || null;
    if (overrideUserIdOrOptions.pullGoogle === false) pullGoogle = false;
    if (overrideUserIdOrOptions.refreshBlocks === false) refreshBlocks = false;
  }

  return { overrideUserId, spaceId, pullGoogle, refreshBlocks };
};

/**
 * Calendar sync for the venue dashboard. Uses venue contact email by default;
 * pass a string email or { spaceId, pullGoogle, refreshBlocks } from home / settings.
 */
export const useCalendarSync = (venueId, venueDetails, fetchBusyTimes, refreshAvailabilityFn) => {
  const { currentUser } = useAuth();
  const [syncLoading, setSyncLoading] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [lastPushTime, setLastPushTime] = useState(null);

  const resolveSyncEmail = useCallback(() => {
    return (
      venueDetails?.contact_email ||
      venueDetails?.contactEmail ||
      currentUser?.email ||
      ''
    );
  }, [venueDetails, currentUser?.email]);

  const runAvailabilityRefresh = useCallback(() => {
    if (typeof refreshAvailabilityFn === 'function') {
      refreshAvailabilityFn();
    }
  }, [refreshAvailabilityFn]);

  const runGoogleBusySync = async (ownerEmail, spaceId) => {
    let result = await syncGoogleCalendarBusy({
      userId: ownerEmail,
      venueId,
      days: 30,
      spaceId
    });

    if (result.needsAuth) {
      await connectGoogleCalendar(ownerEmail);
      result = await syncGoogleCalendarBusy({
        userId: ownerEmail,
        venueId,
        days: 30,
        spaceId
      });
      if (result.needsAuth) {
        throw new Error('Could not sync after connecting Google.');
      }
    }

    if (fetchBusyTimes) {
      await fetchBusyTimes();
    }
    setLastSyncTime(new Date());
    return { count: (result.busyTimes || []).length };
  };

  const syncCalendar = async (overrideUserIdOrOptions) => {
    if (!venueId) {
      alert('No venue ID found');
      return;
    }

    const { overrideUserId, spaceId, pullGoogle, refreshBlocks } =
      parseCalendarActionOptions(overrideUserIdOrOptions);

    const ownerEmail = overrideUserId || resolveSyncEmail();
    if (pullGoogle && !ownerEmail) {
      alert('No Google account email. Set venue contact email or pick an account in Calendar sync settings.');
      return;
    }

    try {
      setSyncLoading(true);
      let syncResult = { count: 0 };
      if (pullGoogle) {
        syncResult = await runGoogleBusySync(ownerEmail, spaceId);
      }
      if (refreshBlocks) {
        runAvailabilityRefresh();
      }
      setSyncLoading(false);
      return pullGoogle ? syncResult : { refreshed: true };
    } catch (error) {
      const message = error?.message || 'Could not sync your calendar. Please try again.';
      alert(message.startsWith('Calendar connection') ? message : `Could not sync your calendar. ${message}`);
      setSyncLoading(false);
    }
  };

  const pushCalendarToGoogle = async (overrideUserIdOrOptions) => {
    if (!venueId) {
      alert('No venue ID found');
      return;
    }

    const { overrideUserId, spaceId } = parseCalendarActionOptions(overrideUserIdOrOptions);
    const ownerEmail = overrideUserId || resolveSyncEmail();
    if (!ownerEmail) {
      alert('No Google account email. Set venue contact email or pick an account in Calendar sync settings.');
      return;
    }

    try {
      setPushLoading(true);
      let result = await pushGoogleCalendarBusy({
        userId: ownerEmail,
        venueId,
        days: 30,
        spaceId
      });

      if (result.needsAuth) {
        await connectGoogleCalendar(ownerEmail);
        result = await pushGoogleCalendarBusy({
          userId: ownerEmail,
          venueId,
          days: 30,
          spaceId
        });
        if (result.needsAuth) {
          throw new Error('Could not push after connecting Google.');
        }
      }

      if (fetchBusyTimes) {
        await fetchBusyTimes();
      }
      runAvailabilityRefresh();
      setLastPushTime(new Date());
      setPushLoading(false);
      const totalWritten = result.totalWritten ?? (result.pushed || 0) + (result.updated || 0);
      return {
        pushed: result.pushed ?? 0,
        updated: result.updated ?? 0,
        failed: result.failed ?? 0,
        totalWritten,
        maps: result.maps,
        message: result.message || '',
        googleEventHtmlLink: result.google_event_html_link || null,
        failures: result.failures || [],
        ok: result.success !== false && (result.failed || 0) === 0
      };
    } catch (error) {
      const message = error?.message || 'Could not push to Google Calendar. Please try again.';
      alert(
        message.startsWith('Calendar connection')
          ? message
          : `Could not push to Google Calendar. ${message}`
      );
      setPushLoading(false);
    }
  };

  /** Refresh Occasia blocks/capacity only — no Google pull. */
  const refreshAvailabilityFromBackend = useCallback(
    () => runAvailabilityRefresh(),
    [runAvailabilityRefresh]
  );

  return {
    syncLoading,
    pushLoading,
    lastSyncTime,
    lastPushTime,
    syncCalendar,
    pushCalendarToGoogle,
    refreshAvailabilityFromBackend,
    resolveSyncEmail
  };
};
